import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { savedArtworksApi } from '../api/savedArtworksApi';

const SavedArtworksContext = createContext();

const transformArtwork = (artwork) => {
  let image = null;
  if (artwork.imageData && artwork.imageMimeType) {
    image = `data:${artwork.imageMimeType};base64,${artwork.imageData}`;
  } else if (artwork.imageUrl) {
    image = artwork.imageUrl;
  }

  return {
    ...artwork,
    image,
    id: artwork._id,
  };
};

export function SavedArtworksProvider({ children }) {
  const [savedArtworks, setSavedArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, isLoading: authLoading } = useAuthContext();

  const loadSavedArtworks = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await savedArtworksApi.getSavedArtworks();
      if (response.success) {
        setSavedArtworks(response.savedArtworks.map(transformArtwork));
      }
    } catch (error) {
      console.error('Error loading saved artworks:', error);
      const saved = localStorage.getItem('savedArtworks');
      if (saved) {
        try {
          setSavedArtworks(JSON.parse(saved));
        } catch {
          /* ignore */
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && !authLoading) {
      loadSavedArtworks();
    } else if (!authLoading && !user) {
      setSavedArtworks([]);
    }
  }, [user, authLoading]);

  const toggleSaveArtwork = async (artwork) => {
    if (!user) return false;

    const artworkId = artwork._id || artwork.id;

    try {
      const response = await savedArtworksApi.toggleSaveArtwork(artworkId);
      if (response.success) {
        setSavedArtworks((prev) => {
          if (response.isSaved) {
            const exists = prev.find((item) => (item._id || item.id) === artworkId);
            return exists ? prev : [...prev, { ...artwork, id: artworkId }];
          }
          return prev.filter((item) => (item._id || item.id) !== artworkId);
        });
        return true;
      }
    } catch (error) {
      console.error('Error toggling saved artwork:', error);
      setSavedArtworks((prev) => {
        const isAlreadySaved = prev.find((item) => (item._id || item.id) === artworkId);
        const next = isAlreadySaved
          ? prev.filter((item) => (item._id || item.id) !== artworkId)
          : [...prev, { ...artwork, id: artworkId }];
        localStorage.setItem('savedArtworks', JSON.stringify(next));
        return next;
      });
      return true;
    }
    return false;
  };

  const isArtworkSaved = (artworkId) =>
    savedArtworks.some((artwork) => (artwork._id || artwork.id) === artworkId);

  return (
    <SavedArtworksContext.Provider
      value={{
        savedArtworks,
        toggleSaveArtwork,
        isArtworkSaved,
        loading,
        loadSavedArtworks,
      }}
    >
      {children}
    </SavedArtworksContext.Provider>
  );
}

export const useSavedArtworks = () => useContext(SavedArtworksContext);
